import React, { useMemo } from "react";

interface ExecutionDevice {
  id: string;
  serial: string;
  model: string;
}

interface ExecutionLite {
  id: string;
  scenarioName: string;
  featureFile?: string | null;
  status: string;
  durationMs: number | null;
  errorMessage: string | null;
  attempt?: number;
  device?: ExecutionDevice | null;
}

interface Props {
  executions: ExecutionLite[];
  totalScenarios?: number;
}

/**
 * Bucket free-form errors into stable categories. Pattern-matching is
 * intentionally simple — we want stable categories, not exhaustive coverage.
 */
function bucketError(err: string | null | undefined): string {
  if (!err) return "(empty error)";
  if (err.includes("No activities in stage RESUMED")) {
    return "Espresso: activity not RESUMED";
  }
  if (err.includes("home screen not found")) return "login: no home screen";
  if (err.includes("No instrumentation runner found")) {
    return "Detox: instrumentation missing";
  }
  if (err.includes("has-window-focus=false")) {
    return "Espresso: no window focus";
  }
  if (err.includes("Failed to run application")) {
    return "Detox: app launch failed";
  }
  if (/timeout expired without matching/i.test(err)) {
    return "Detox: element wait timeout";
  }
  if (/function timed out, ensure the promise/i.test(err)) {
    return "Cucumber: scenario timeout";
  }
  if (err.includes("AssertionError")) return "AssertionError";
  if (err.includes("ECONNREFUSED")) return "Network: ECONNREFUSED";
  return err.slice(0, 60).replace(/\s+/g, " ").trim() + (err.length > 60 ? "…" : "");
}

const cardStyle: React.CSSProperties = {
  background: "var(--color-surface)",
  borderRadius: "var(--radius-md)",
  boxShadow: "var(--shadow-card)",
  padding: "var(--space-md)",
};

const labelStyle: React.CSSProperties = {
  fontSize: "var(--font-size-xs)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: "var(--color-text-secondary)",
  margin: 0,
  marginBottom: 4,
};

const tinyStyle: React.CSSProperties = {
  fontSize: "var(--font-size-xs)",
  color: "var(--color-text-secondary)",
};

function formatSec(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m${Math.round((ms % 60000) / 1000)}s`;
}

function shortFeature(path: string | null | undefined): string {
  if (!path) return "(no feature)";
  // strip leading paths so AccountSettings/about.feature → AccountSettings/about
  const m = path.match(/features\/(.+?)(\.feature)?$/);
  return m ? m[1] : path;
}

export default function RunStats({ executions, totalScenarios }: Props) {
  const stats = useMemo(() => {
    let passed = 0;
    let failed = 0;
    let skipped = 0;
    let durSum = 0;
    let durCount = 0;
    const perDevice = new Map<
      string,
      {
        serial: string;
        model: string;
        passed: number;
        failed: number;
        durSum: number;
        durCount: number;
      }
    >();
    const errorBuckets = new Map<string, number>();
    const scenarioFailCount = new Map<string, number>();
    const perFeature = new Map<
      string,
      { feature: string; passed: number; failed: number }
    >();
    const slowest: Array<{
      scenario: string;
      durationMs: number;
      status: string;
      serial: string;
    }> = [];

    for (const e of executions) {
      if (e.status === "passed") passed++;
      else if (e.status === "failed") failed++;
      else if (e.status === "skipped") skipped++;

      if (e.durationMs && e.status !== "skipped") {
        durSum += e.durationMs;
        durCount++;
        slowest.push({
          scenario: e.scenarioName,
          durationMs: e.durationMs,
          status: e.status,
          serial: e.device?.serial ?? "?",
        });
      }

      const dev = e.device;
      if (dev) {
        const key = dev.serial || dev.id;
        const cur =
          perDevice.get(key) ??
          { serial: dev.serial, model: dev.model, passed: 0, failed: 0, durSum: 0, durCount: 0 };
        if (e.status === "passed") cur.passed++;
        else if (e.status === "failed") cur.failed++;
        if (e.durationMs && e.status !== "skipped") {
          cur.durSum += e.durationMs;
          cur.durCount++;
        }
        perDevice.set(key, cur);
      }

      if (e.status === "failed") {
        const bucket = bucketError(e.errorMessage);
        errorBuckets.set(bucket, (errorBuckets.get(bucket) ?? 0) + 1);
        scenarioFailCount.set(
          e.scenarioName,
          (scenarioFailCount.get(e.scenarioName) ?? 0) + 1
        );
      }

      const featKey = shortFeature(e.featureFile);
      const feat = perFeature.get(featKey) ?? { feature: featKey, passed: 0, failed: 0 };
      if (e.status === "passed") feat.passed++;
      else if (e.status === "failed") feat.failed++;
      perFeature.set(featKey, feat);
    }

    const finished = passed + failed + skipped;
    const planned = totalScenarios ?? finished;

    // AIDEV-NOTE: Two pass-rate views.
    //
    // 1. Aggregate (final-outcome) pass rate — % of UNIQUE scenarios that
    //    eventually passed across all attempts. Retries that recovered a
    //    failing scenario count as PASSES here. This is the rate that
    //    answers "did the suite succeed?" and the number that should NOT
    //    decline as retries run.
    //
    // 2. Per-attempt pass rate — % of executions in a given attempt that
    //    passed. Useful for "is attempt 1 stable?" or "is the retry path
    //    actually rescuing failures?" Attempt 1 is usually the headline;
    //    attempt 2+ are typically much lower because they only re-run the
    //    hardest failures.
    //
    // The naive `passed / finished` over ALL executions declines as retries
    // run because each retry adds an execution row — that's the regression
    // shown in the dashboard's PASS RATE card today.
    const latestByScenario = new Map<string, ExecutionLite>();
    for (const e of executions) {
      const key = e.scenarioName;
      const prev = latestByScenario.get(key);
      const prevAttempt = prev?.attempt ?? 0;
      const curAttempt = e.attempt ?? 0;
      if (!prev || curAttempt >= prevAttempt) latestByScenario.set(key, e);
    }
    let aggPassed = 0;
    let aggFailed = 0;
    let aggSkipped = 0;
    for (const e of latestByScenario.values()) {
      if (e.status === "passed") aggPassed++;
      else if (e.status === "failed") aggFailed++;
      else if (e.status === "skipped") aggSkipped++;
    }
    const aggUniqueExecuted = aggPassed + aggFailed + aggSkipped;
    // The denominator is UNIQUE scenarios that actually ran to a verdict —
    // passed or failed. Skipped scenarios are excluded because they are
    // either quick-run cache hits (would have passed) or platform-skip
    // (genuine N/A, e.g. @web on Android). Counting skips in the
    // denominator makes the pass-rate look terrible even when every
    // executed scenario passed.
    //
    // We deliberately do NOT use the server's `totalScenarios` field
    // here because the server currently counts every execution row
    // (including retries), so it inflates as retries run. The "Planned"
    // line in the breakdown still surfaces the server value so the
    // discrepancy is visible while it's being fixed server-side.
    const aggDenominator = aggPassed + aggFailed;
    const aggPassRate = aggDenominator > 0
      ? Math.round((aggPassed / aggDenominator) * 100)
      : 0;

    // Per-attempt breakdown. Skipped scenarios don't count for/against the
    // attempt's rate — they were filtered out before execution.
    const perAttempt = new Map<
      number,
      { passed: number; failed: number; skipped: number }
    >();
    for (const e of executions) {
      const a = e.attempt ?? 1;
      const cur = perAttempt.get(a) ?? { passed: 0, failed: 0, skipped: 0 };
      if (e.status === "passed") cur.passed++;
      else if (e.status === "failed") cur.failed++;
      else if (e.status === "skipped") cur.skipped++;
      perAttempt.set(a, cur);
    }
    const attemptRows = Array.from(perAttempt.entries())
      .sort(([a], [b]) => a - b)
      .map(([attempt, c]) => {
        const total = c.passed + c.failed;
        return {
          attempt,
          passed: c.passed,
          failed: c.failed,
          total,
          rate: total > 0 ? Math.round((c.passed / total) * 100) : 0,
        };
      });

    // Keep the legacy single-rate field for any existing consumer, but it
    // now reflects the AGGREGATE rate (the one users actually want).
    const passRate = aggPassRate;
    const avgDurMs = durCount > 0 ? Math.round(durSum / durCount) : 0;
    const totalDeviceMinutes = durSum / 60_000;
    const minutesPerPass = passed > 0 ? totalDeviceMinutes / passed : 0;

    // p95 duration
    slowest.sort((a, b) => b.durationMs - a.durationMs);
    const p95Idx = Math.floor(slowest.length * 0.05);
    const p95DurMs = slowest[p95Idx]?.durationMs ?? 0;

    const devices = Array.from(perDevice.values()).sort((a, b) => {
      const ratioA = a.failed / Math.max(1, a.passed + a.failed);
      const ratioB = b.failed / Math.max(1, b.passed + b.failed);
      return ratioB - ratioA;
    });

    const features = Array.from(perFeature.values())
      .map((f) => ({
        ...f,
        rate: f.passed + f.failed > 0 ? Math.round((f.passed / (f.passed + f.failed)) * 100) : 0,
      }))
      .sort((a, b) => a.rate - b.rate || (b.failed - a.failed))
      .slice(0, 8);

    const topErrors = Array.from(errorBuckets.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    const topFailingScenarios = Array.from(scenarioFailCount.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    // Auto-detected issues — heuristics over the data.
    const issues: string[] = [];

    // Show enough of the serial to disambiguate when devices share a prefix
    // (e.g. RFGYC1TGRPT vs RFGYC1TGRWJ — first 8 chars collide).
    const allSerials = devices.map((d) => d.serial);
    const shortSerial = (s: string): string => {
      const collisionAt8 = allSerials.filter((x) => x.slice(0, 8) === s.slice(0, 8)).length > 1;
      return collisionAt8 ? s : s.slice(0, 8);
    };

    if (devices.length >= 2) {
      const failRates = devices.map((d) =>
        d.passed + d.failed > 0 ? d.failed / (d.passed + d.failed) : 0
      );
      const median = [...failRates].sort()[Math.floor(failRates.length / 2)];
      const outliers = devices.filter((d) => {
        const total = d.passed + d.failed;
        if (total < 3) return false;
        const rate = d.failed / total;
        return rate > 0.4 && (median === 0 || rate > median * 2);
      });
      for (const o of outliers) {
        const total = o.passed + o.failed;
        const rate = Math.round((o.failed / total) * 100);
        issues.push(
          `${o.model} (${shortSerial(o.serial)}) failing at ${rate}% — investigate device or its hub bus`
        );
      }
    }
    const resumedCount = errorBuckets.get("Espresso: activity not RESUMED") ?? 0;
    if (resumedCount > 0) {
      issues.push(
        `${resumedCount}× "activity not RESUMED" — usually a permission dialog or overlay stealing focus`
      );
    }
    if (devices.length >= 2 && durCount > 0) {
      const deviceAvgs = devices
        .filter((d) => d.durCount > 0)
        .map((d) => ({
          serial: d.serial,
          model: d.model,
          avg: d.durSum / d.durCount,
        }));
      if (deviceAvgs.length >= 2) {
        const avgs = deviceAvgs.map((d) => d.avg);
        const median = [...avgs].sort()[Math.floor(avgs.length / 2)];
        const slow = deviceAvgs.filter((d) => d.avg > median * 2);
        for (const s of slow) {
          issues.push(
            `${s.model} (${shortSerial(s.serial)}) is ${(s.avg / median).toFixed(1)}× slower than median — possible USB bus saturation`
          );
        }
      }
    }
    if (planned > finished + 1 && finished > 0) {
      const remaining = planned - finished;
      issues.push(
        `${remaining} scenario(s) still pending — run is in progress`
      );
    }

    return {
      passed,
      failed,
      skipped,
      finished,
      planned,
      passRate,
      aggPassed,
      aggFailed,
      aggSkipped,
      aggUniqueExecuted,
      aggDenominator,
      aggPassRate,
      attemptRows,
      avgDurMs,
      p95DurMs,
      totalDeviceMinutes,
      minutesPerPass,
      devices,
      topErrors,
      topFailingScenarios,
      features,
      issues,
      slowest: slowest.slice(0, 5),
    };
  }, [executions, totalScenarios]);

  if (stats.finished === 0 && stats.planned === 0) return null;

  const passRateColor =
    stats.passRate >= 90
      ? "var(--color-success)"
      : stats.passRate >= 70
      ? "var(--color-warning)"
      : "var(--color-danger)";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-md)", marginBottom: "var(--space-md)" }}>
      {/* Top row: pass rate + per-device + hotspots */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(220px, 1fr) minmax(280px, 2fr) minmax(280px, 2fr)",
          gap: "var(--space-md)",
        }}
      >
        {/* Card 1: pass-rate + totals.
            Headline number is the AGGREGATE (final-outcome) pass rate —
            retries that rescued a failing scenario count as passes here.
            Per-attempt rates appear below so users can still see how
            stable attempt 1 was and how much retries recovered. */}
        <div style={cardStyle}>
          <div style={labelStyle}>Pass rate</div>
          <div
            style={{
              fontSize: "var(--font-size-xl)",
              fontWeight: 700,
              color: passRateColor,
              marginBottom: 8,
            }}
          >
            {stats.aggPassRate}%
            <span
              style={{
                fontSize: "var(--font-size-sm)",
                fontWeight: 400,
                color: "var(--color-text-secondary)",
                marginLeft: 8,
              }}
            >
              {stats.aggPassed} / {stats.aggDenominator}
            </span>
          </div>
          <div
            style={{
              height: 8,
              background: "var(--color-status-busy-bg)",
              borderRadius: 4,
              overflow: "hidden",
              marginBottom: 12,
            }}
          >
            <div
              style={{
                width: `${stats.aggPassRate}%`,
                height: "100%",
                background: passRateColor,
                transition: "width 200ms ease",
              }}
            />
          </div>
          <div
            style={{
              ...tinyStyle,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 4,
              marginBottom: stats.attemptRows.length > 1 ? 12 : 0,
            }}
          >
            <div>Planned: {stats.planned}</div>
            <div>Finished: {stats.aggUniqueExecuted}</div>
            <div>Failed: {stats.aggFailed}</div>
            <div>Skipped: {stats.aggSkipped}</div>
          </div>
          {stats.attemptRows.length > 1 && (
            <div style={{ borderTop: "1px solid var(--color-foreground)", paddingTop: 8 }}>
              <div style={{ ...labelStyle, marginBottom: 6 }}>
                By attempt
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {stats.attemptRows.map((row) => {
                  const color =
                    row.rate >= 90
                      ? "var(--color-success)"
                      : row.rate >= 70
                      ? "var(--color-warning)"
                      : "var(--color-danger)";
                  return (
                    <div
                      key={row.attempt}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "auto 1fr auto",
                        gap: 8,
                        alignItems: "center",
                        fontSize: "var(--font-size-xs)",
                      }}
                    >
                      <span style={{ color: "var(--color-text-secondary)" }}>
                        Attempt {row.attempt}
                      </span>
                      <div
                        style={{
                          height: 6,
                          background: "var(--color-status-busy-bg)",
                          borderRadius: 3,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${row.rate}%`,
                            height: "100%",
                            background: color,
                          }}
                        />
                      </div>
                      <span style={{ color, fontWeight: 600 }}>
                        {row.rate}%{" "}
                        <span
                          style={{
                            color: "var(--color-text-secondary)",
                            fontWeight: 400,
                          }}
                        >
                          {row.passed}/{row.total}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Card 2: per-device breakdown — hot spots highlighted by sort order */}
        <div style={cardStyle}>
          <div style={{ ...labelStyle, marginBottom: 8 }}>
            Per-device — {stats.devices.length} active
          </div>
          {stats.devices.length === 0 ? (
            <div style={tinyStyle}>No device assignments yet</div>
          ) : (
            <table style={{ width: "100%", fontSize: "var(--font-size-xs)", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", color: "var(--color-text-secondary)" }}>
                  <th style={{ padding: "2px 4px" }}>device</th>
                  <th style={{ padding: "2px 4px", textAlign: "right" }}>pass</th>
                  <th style={{ padding: "2px 4px", textAlign: "right" }}>fail</th>
                  <th style={{ padding: "2px 4px", textAlign: "right" }}>%</th>
                  <th style={{ padding: "2px 4px", textAlign: "right" }}>avg</th>
                </tr>
              </thead>
              <tbody>
                {stats.devices.map((d) => {
                  const total = d.passed + d.failed;
                  const rate = total > 0 ? Math.round((d.passed / total) * 100) : 0;
                  const rateColor =
                    rate >= 90
                      ? "var(--color-success)"
                      : rate >= 70
                      ? "var(--color-warning)"
                      : "var(--color-danger)";
                  const avgS =
                    d.durCount > 0 ? `${(d.durSum / d.durCount / 1000).toFixed(1)}s` : "—";
                  return (
                    <tr key={d.serial} style={{ borderTop: "var(--border-default)" }}>
                      <td
                        style={{
                          padding: "4px",
                          fontFamily: "var(--font-mono, monospace)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 200,
                        }}
                      >
                        {d.model} ({d.serial.slice(0, 8)})
                      </td>
                      <td style={{ padding: "4px", textAlign: "right", color: "var(--color-success)" }}>
                        {d.passed}
                      </td>
                      <td
                        style={{
                          padding: "4px",
                          textAlign: "right",
                          color: d.failed > 0 ? "var(--color-danger)" : "var(--color-text-secondary)",
                        }}
                      >
                        {d.failed}
                      </td>
                      <td style={{ padding: "4px", textAlign: "right", color: rateColor, fontWeight: 600 }}>
                        {rate}%
                      </td>
                      <td style={{ padding: "4px", textAlign: "right", color: "var(--color-text-secondary)" }}>
                        {avgS}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Card 3: failure hotspots */}
        <div style={cardStyle}>
          <div style={{ ...labelStyle, marginBottom: 8 }}>Failure hotspots</div>
          {stats.failed === 0 ? (
            <div style={{ fontSize: "var(--font-size-xs)", color: "var(--color-success)" }}>
              ✓ No failures
            </div>
          ) : (
            <>
              <div style={{ ...tinyStyle, marginBottom: 4 }}>Top error categories</div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", marginBottom: 8 }}>
                {stats.topErrors.map(([bucket, count]) => (
                  <li
                    key={bucket}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "var(--font-size-xs)",
                      padding: "2px 0",
                    }}
                  >
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, marginRight: 8 }}>
                      {bucket}
                    </span>
                    <span style={{ color: "var(--color-danger)", fontWeight: 600 }}>×{count}</span>
                  </li>
                ))}
              </ul>
              {stats.topFailingScenarios.length > 0 && (
                <>
                  <div style={{ ...tinyStyle, marginBottom: 4 }}>Most-failing scenarios</div>
                  <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                    {stats.topFailingScenarios.map(([scn, count]) => (
                      <li
                        key={scn}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "var(--font-size-xs)",
                          padding: "2px 0",
                        }}
                      >
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, marginRight: 8 }}>
                          {scn}
                        </span>
                        <span style={{ color: "var(--color-danger)", fontWeight: 600 }}>×{count}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Second row: auto-issues + slow scenarios + per-feature + resource fingerprint */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(280px, 2fr) minmax(280px, 2fr) minmax(220px, 1fr)",
          gap: "var(--space-md)",
        }}
      >
        {/* Auto-detected issues */}
        <div style={cardStyle}>
          <div style={{ ...labelStyle, marginBottom: 8 }}>Heuristics — auto-detected</div>
          {stats.issues.length === 0 ? (
            <div style={{ fontSize: "var(--font-size-xs)", color: "var(--color-success)" }}>
              ✓ Nothing flagged
            </div>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {stats.issues.map((msg, i) => (
                <li
                  key={i}
                  style={{
                    fontSize: "var(--font-size-xs)",
                    padding: "4px 0",
                    borderTop: i > 0 ? "var(--border-default)" : "none",
                    color: "var(--color-warning)",
                  }}
                >
                  ⚠ {msg}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Slowest scenarios + per-feature */}
        <div style={cardStyle}>
          <div style={{ ...labelStyle, marginBottom: 8 }}>By feature & slowest scenarios</div>
          {stats.features.length > 0 && (
            <>
              <div style={{ ...tinyStyle, marginBottom: 4 }}>Lowest-pass-rate features</div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", marginBottom: 8 }}>
                {stats.features.slice(0, 4).map((f) => {
                  const total = f.passed + f.failed;
                  const c =
                    f.rate >= 90 ? "var(--color-success)" : f.rate >= 70 ? "var(--color-warning)" : "var(--color-danger)";
                  return (
                    <li
                      key={f.feature}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "var(--font-size-xs)",
                        padding: "2px 0",
                        gap: 8,
                      }}
                    >
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                        {f.feature}
                      </span>
                      <span style={{ color: c, fontWeight: 600, minWidth: 80, textAlign: "right" }}>
                        {f.rate}% ({f.passed}/{total})
                      </span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          {stats.slowest.length > 0 && (
            <>
              <div style={{ ...tinyStyle, marginBottom: 4 }}>Slowest scenarios</div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {stats.slowest.map((s, i) => (
                  <li
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "var(--font-size-xs)",
                      padding: "2px 0",
                      gap: 8,
                    }}
                  >
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                      {s.scenario}
                    </span>
                    <span style={{ color: "var(--color-text-secondary)", fontFamily: "var(--font-mono, monospace)" }}>
                      {formatSec(s.durationMs)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* Resource fingerprint */}
        <div style={cardStyle}>
          <div style={{ ...labelStyle, marginBottom: 8 }}>Resource use</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              rowGap: 6,
              columnGap: 8,
              fontSize: "var(--font-size-xs)",
            }}
          >
            <span style={{ color: "var(--color-text-secondary)" }}>Avg scenario:</span>
            <span style={{ fontFamily: "var(--font-mono, monospace)" }}>
              {stats.avgDurMs > 0 ? formatSec(stats.avgDurMs) : "—"}
            </span>
            <span style={{ color: "var(--color-text-secondary)" }}>p95:</span>
            <span style={{ fontFamily: "var(--font-mono, monospace)" }}>
              {stats.p95DurMs > 0 ? formatSec(stats.p95DurMs) : "—"}
            </span>
            <span style={{ color: "var(--color-text-secondary)" }}>Device-min:</span>
            <span style={{ fontFamily: "var(--font-mono, monospace)" }}>
              {stats.totalDeviceMinutes.toFixed(1)}
            </span>
            <span style={{ color: "var(--color-text-secondary)" }}>Min/pass:</span>
            <span style={{ fontFamily: "var(--font-mono, monospace)" }}>
              {stats.minutesPerPass > 0 ? stats.minutesPerPass.toFixed(2) : "—"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
